import { useCallback } from 'react';
import { useCopilotFileDownload } from '@customerio/hydra-copilot/react';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { sentryCaptureException } from '@/utils/sentry';
import { extractFilename, fetchAsBlob } from '../helpers/file-utils';

/**
 * Hook to handle file downloads for copilot files.
 * Handles blob URLs, File objects, and filenames (which need to be fetched).
 */
export function useCopilotHandleDownload(options: {
  source: File | string;
  filename: string;
  session?: CopilotSession;
}) {
  const { source, filename, session } = options;
  const { getFileDownloadUrl } =
    session != null ? useCopilotFileDownload(session) : { getFileDownloadUrl: async () => '' };

  const handleDownload = useCallback(async () => {
    try {
      let downloadUrl: string;

      if (typeof source === 'string') {
        // Check if it's already a blob URL
        if (source.startsWith('blob:')) {
          // Use existing blob URL
          downloadUrl = source;
        } else {
          // It's a filename or gs:// path - extract filename and fetch signed URL
          const filename = extractFilename(source);
          const signedUrl = await getFileDownloadUrl(filename);
          downloadUrl = await fetchAsBlob(signedUrl);
        }
      } else {
        // It's a File object - create a blob URL from it
        downloadUrl = URL.createObjectURL(source);
      }

      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.dispatchEvent(
        new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
        }),
      );
      document.body.removeChild(link);

      // Revoke the blob URL after download (unless it was a pre-existing blob from thumbnail)
      if (typeof source !== 'string' || !source.startsWith('blob:')) {
        setTimeout(() => {
          URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
    } catch (error) {
      sentryCaptureException(error);
      throw error;
    }
  }, [source, filename, getFileDownloadUrl]);

  return { handleDownload };
}
